import { type AllProductDTO, type CartItem, type MergedCouponCriterion } from '@cart-services/types'
import { CouponCondition } from '@cart-ui/CouponCondition/CouponCondition'
import { type ProjectCode } from '@constants'
import { AppliedStatus, type Coupon, type CouponType } from '@hooks-api/types/dataModel/coupon'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Chip } from '@pets-ui/Chip/Chip'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { sumOfArray } from '@utils/sumOfArray'
import { useEffect, useMemo, useState } from 'react'
import { type ItemWantToAdd, useHandleProductAddToCart } from '../../../fp-b2b-shared/hooks/use-handle-product-add-to-cart'
import { useProductCatalog } from '../../../fp-b2b-shared/hooks/use-product-catalog'
import { ProductVariationRow } from '../../components/ProductVariationRow'
import { hasAnyNewProductVariation } from '../../utils/new-product-filter'
import { StmCollapseProductButton } from './StmCollapseProductButton'

export interface StmCollapseProductProps {
  parentProductGroup: AllProductDTO
  projectCode: ProjectCode
  cartItems: CartItem[]
  getMergedCouponCriterions: (couponIds: Coupon['id'][], types?: CouponType[]) => MergedCouponCriterion[]
}

export const StmCollapseProduct = ({
  parentProductGroup,
  projectCode,
  cartItems,
  getMergedCouponCriterions,
}: StmCollapseProductProps) => {
  const { currency } = useProductCatalog()
  const { handleProductAddToCart } = useHandleProductAddToCart()
  const [open, setOpen] = useState(true)
  const firstLevelButtonKeys = useMemo(() => Object.keys(parentProductGroup.variationsGroupByFirstLevelButton), [parentProductGroup.variationsGroupByFirstLevelButton])
  const [currentGroup, setCurrentGroup] = useState<string>()
  const currentVariations = useMemo(() => currentGroup ? parentProductGroup.variationsGroupByFirstLevelButton[currentGroup] : [], [currentGroup, parentProductGroup.variationsGroupByFirstLevelButton])

  const countProduct = useMemo(() => sumOfArray(cartItems, (acc, { product_id, quantity = 0 }) => {
    if (product_id === parentProductGroup.id) {
      return acc + quantity
    }
    return acc
  }), [cartItems, parentProductGroup.id])

  const handleCollapse = () => {
    setOpen(!open)
  }

  const handleQuantityChange = (variationItem: Variation) => (quantity?: number) => {
    const itemWantToAdd: ItemWantToAdd = {
      ...variationItem,
      delivery_class: variationItem.delivery_class,
      cartItemId: variationItem.variation_id,
      quantity,
    }
    handleProductAddToCart(itemWantToAdd)
  }

  const couponIds = useMemo(() => [...new Set(currentVariations?.flatMap(variation => variation.coupon_ids || []) || [])], [currentVariations])
  const couponConditions = useMemo(() => getMergedCouponCriterions(couponIds), [couponIds, getMergedCouponCriterions])
  const buttonBaseId = parentProductGroup.title ? projectCode + '-' + parentProductGroup.title : ''
  const showNewTag = hasAnyNewProductVariation(parentProductGroup)

  useEffect(() => {
    if (firstLevelButtonKeys.length > 0) {
      setCurrentGroup(firstLevelButtonKeys[0])
    }
    return () => {
      setCurrentGroup(undefined)
    }
  }, [firstLevelButtonKeys])

  return (
    <>
      <StmCollapseProductButton
        id={buttonBaseId}
        onClick={handleCollapse}
        thumbnail={parentProductGroup.last_gallery_image || parentProductGroup.thumbnail}
        title={parentProductGroup.title}
        countProduct={countProduct}
        showProductSalesTag={couponIds.length > 0}
        showProductNewTag={showNewTag}
        open={open}
      />
      <Collapse in={open}>
        <VStack marginTop="8px">
          {couponConditions.length > 0 && (
            <VStack gap={2} p={2}>
              {couponConditions.map(criterion => (
                <CouponCondition
                  key={criterion.display_name}
                  isApplied={criterion.applied_status === AppliedStatus.APPLIED || criterion.applied_status === AppliedStatus.UNABLE}
                  displayName={criterion.display_name}
                  currency={currency}
                  moreAmount={criterion.left_amount}
                  moreQuantity={criterion.left_quantity ?? criterion.display_criterion_quantity}
                  count={criterion.count}
                />
              ))}
            </VStack>
          )}
          {firstLevelButtonKeys.length > 1 && (
            <HStack gap="8px" padding="8px">
              {firstLevelButtonKeys.map((firstLevelButton) => {
                const groupId = projectCode + '-' + parentProductGroup.title + '-' + firstLevelButton
                const isSelected = firstLevelButton === currentGroup
                return (
                  <Chip
                    id={groupId}
                    key={firstLevelButton}
                    p={1}
                    variant="square"
                    color={isSelected ? 'red' : 'default'}
                    isOutlined
                    isSoft={isSelected}
                    onClick={() => setCurrentGroup(firstLevelButton)}
                  >
                    {firstLevelButton}
                  </Chip>
                )
              })}
            </HStack>
          )}
          <VStack backgroundColor={colors.grey50} divider={<Divider color={colors.grey200} />}>
            {currentVariations?.map((variation) => {
              const currentQty = cartItems.find(item => item.cartItemId === variation.variation_id)?.quantity || 0
              const productId = projectCode + '-' + variation.sku
              return (
                <ProductVariationRow
                  key={variation.sku}
                  id={productId}
                  variation={variation}
                  currentQuantity={currentQty}
                  onQuantityChange={handleQuantityChange(variation)}
                />
              )
            })}
          </VStack>
        </VStack>
      </Collapse>
    </>
  )
}
